import {
  Calendar,
  Clipboard,
  Gift,
  Megaphone,
  MessageSquareMore,
  Home,
  CalendarCheck,
  DoorOpen,
} from "lucide-react";
import { useEffect, useMemo, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { Aniversariantes } from "./Aniversariantes";
import { ButtonNotificacoes } from "./Notificacoes/sections/ButtonNotificacoes";
import { Campanhas } from "./Campanhas/Campanhas";
import { Notificacoes } from "./Notificacoes";
import { Reunioes } from "./Reunioes";
import { Tarefas } from "@/components/Tarefas";
import { useLayout } from "../../contexts/UseLayout";
import { NotebookPen } from "lucide-react";
import { Chat } from "./Chat";
import { NotificationBalloon } from "./Chat/sections/NotificationBalloon";
import { Notas } from "./Notas/Notas";
import { ChatWindowImproved } from "./Chat/sections/ChatWindowImproved";
import TemPermissao from "@/components/utils/NiveisDeAcesso";

export function LayoutMenu({
  menuOpen,
  setMenuOpen,
  selectedOption,
  setSelectedOption,
}: any) {
  const { t } = useTranslation();
  const context: any = useLayout();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<any[]>([]); // Estado para notificações
  const previousChatsRef = useRef<any[]>([]);
  const isInitialLoad = useRef(true); // Flag para identificar primeira carga

  // Detectar novas mensagens e mostrar notificação
  useEffect(() => {
    if (!context.chats?.length) return;

    // Na primeira carga, apenas inicializar previousChats sem mostrar notificações
    if (isInitialLoad.current) {
      previousChatsRef.current = [...context.chats];
      isInitialLoad.current = false;
      return;
    }

    const currentPrevChats = previousChatsRef.current;
    const newNotifications: any[] = [];

    context.chats.forEach((chat: any) => {
      const previousChat = currentPrevChats.find(
        (prev: any) => prev.id === chat.id,
      );

      // Verificar se há nova mensagem não lida
      const hasNewMessage =
        previousChat &&
        chat.unread_count > (previousChat.unread_count || 0) &&
        chat.lastMessage &&
        chat.lastMessage.sender_id !== context.userLogged.data?.id;

      // Verificar se é um chat novo com mensagens não lidas
      const isNewChatWithMessages =
        !previousChat &&
        chat.unread_count > 0 &&
        chat.lastMessage &&
        chat.lastMessage.sender_id !== context.userLogged.data?.id;

      if (hasNewMessage || isNewChatWithMessages) {
        const senderName = chat.lastMessage?.sender
          ? `${chat.lastMessage.sender.name} ${chat.lastMessage.sender.last_name || ""}`.trim()
          : chat.name || "Usuário";
        const messageContent = chat.lastMessage?.content || "Nova mensagem";
        const senderImage =
          chat.lastMessage?.sender?.url_img?.path || chat.group_image || null;

        newNotifications.push({
          senderName,
          message: messageContent,
          senderImage,
          chatId: chat.id,
        });
      }
    });

    if (newNotifications.length > 0) {
      setNotifications((prev) => [...prev, ...newNotifications]);
    }

    // Atualizar ref sem causar re-render
    previousChatsRef.current = [...context.chats];
  }, [context.chats, context.userLogged.data?.id]);

  const handleCloseNotification = (index: number) => {
    setNotifications((prev) => prev.filter((_, i) => i !== index));
  };

  const totalUnreadMessages = useMemo(() => {
    return context.chats?.reduce((total: number, chat: any) => {
      return total + (chat.unread_count || 0);
    }, 0);
  }, [context.chats]);

  const menus = [
    {
      id: "home",
      title: t("menu.home"),
      icon: <Home size={20} />,
      route: "/overview",
      visible: TemPermissao(context.userLogged, "overview", "visualizar"),
    },

    {
      id: "tarefas",
      title: t("menu.tasks"),
      icon: (
        <>
          <div className={`relative`}>
            <Clipboard size={20} />
            {Number(localStorage.getItem("qtd_tarefas")) > 0 && (
              <div className="bg-red-500 text-white text-[8px] rounded-full h-4 w-4 flex items-center justify-center absolute -top-2 -right-2">
                {localStorage.getItem("qtd_tarefas")}
              </div>
            )}
          </div>
        </>
      ),
      route: "/tarefas",
      component: <Tarefas />,
      visible: TemPermissao(context.userLogged, "tarefas", "visualizar"),
    },
    {
      id: "notas",
      title: t("menu.notes"),
      icon: <NotebookPen size={20} />,
      route: "/notas",
      component: <Notas />,
      visible: TemPermissao(context.userLogged, "notas", "visualizar"),
    },
    {
      id: "reunioes",
      title: t("menu.meetings"),
      icon: (
        <>
          <div className={`relative`}>
            <Calendar size={20} />
            {Number(localStorage.getItem("qtd_reunioes")) > 0 && (
              <div className="bg-red-500 text-white text-[8px] rounded-full h-4 w-4 flex items-center justify-center absolute -top-2 -right-2">
                {localStorage.getItem("qtd_reunioes")}
              </div>
            )}
          </div>
        </>
      ),
      route: "/reunioes",
      component: <Reunioes />,
      visible: TemPermissao(context.userLogged, "reunioes", "visualizar"),
    },
    {
      id: "salas-reuniao",
      title: "Salas de reunião",
      icon: <DoorOpen size={20} />,
      route: "/reunioes/salas",
      visible: TemPermissao(context.userLogged, "reunioes", "visualizar"),
    },
    {
      id: "cronograma-social",
      title: "Cronograma Social",
      icon: <CalendarCheck size={20} />,
      route: "/cronograma",
      visible: TemPermissao(
        context.userLogged,
        "cronograma-social",
        "visualizar",
      ),
    },
    {
      id: "aniversariantes",
      title: t("menu.birthdays"),
      icon: <Gift size={20} />,
      route: "/aniversariantes",
      component: <Aniversariantes idMenu={selectedOption || ""} />,
      visible: TemPermissao(
        context.userLogged,
        "aniversariantes",
        "visualizar",
      ),
    },
    {
      id: "notificacoes",
      title: t("menu.notifications"),
      icon: <ButtonNotificacoes />,
      route: "/notificacoes",
      component: <Notificacoes />,
      visible: true, // Notificações sempre visíveis
    },
    {
      id: "campanhas",
      title: t("menu.campaigns"),
      icon: <Megaphone size={20} />,
      route: "/campanhas",
      component: <Campanhas idMenu={selectedOption || ""} />,
      visible: TemPermissao(context.userLogged, "campanhas", "visualizar"),
    },
    {
      id: "mensagem",
      title: t("menu.messages"),
      icon: (
        <div className={`relative`}>
          <MessageSquareMore size={20} />
          {totalUnreadMessages > 0 && (
            <div className="bg-red-500 text-white text-[8px] rounded-full h-4 w-4 flex items-center justify-center absolute -top-2 -right-2">
              {totalUnreadMessages}
            </div>
          )}
        </div>
      ),
      route: "/mensagens",
      component: <Chat idMenu={selectedOption || ""} />,
      visible: TemPermissao(context.userLogged, "chat", "visualizar"),
    },
  ];

  const filteredMenus: any = menus.filter((menu) => menu.visible);

  const filteredMobile: any = menus.filter(
    (menu) => menu.visible && menu.id !== "aniversariantes",
  );

  const selectedOptionData = filteredMenus.find(
    (o: any) => o?.id === selectedOption,
  );

  const [minimizedChats, setMinimizedChats] = useState<any[]>([]); // Estado para chats minimizados

  const handleMinimizeChat = (roomId: any) => {
    if (!minimizedChats.includes(roomId)) {
      setMinimizedChats([...minimizedChats, roomId]);
    }
  };

  const handleMaximizeChat = (roomId: any) => {
    setMinimizedChats(minimizedChats.filter((id) => id !== roomId));
  };

  const handleCloseChat = (
    roomId: any,
    options?: {
      preserveCall?: boolean;
    },
  ) => {
    if (!options?.preserveCall) {
      void context.closeChatCallForRoom(roomId, "chat_window_closed");
    }

    context.setOpenChats((prev: any[]) =>
      prev.filter((chat: any) => chat.room.id !== roomId),
    );
    setMinimizedChats(minimizedChats.filter((id) => id !== roomId));
  };

  return (
    <>
      {notifications.map((notification, index) => (
        <NotificationBalloon
          key={index}
          message={notification.message}
          senderName={notification.senderName}
          senderImage={notification.senderImage}
          onClose={() => handleCloseNotification(index)}
        />
      ))}
      <div className="sm:hidden fixed bottom-0 left-0 w-full bg-white border-t border-gray-300 flex justify-around py-2">
        {filteredMobile?.map((menu: any) => (
          <button
            key={menu.id}
            title={menu.title}
            className="flex flex-col items-center text-gray-700"
            onClick={() => navigate(menu.route)}
          >
            {menu.icon}
            <span className="text-xs">{menu.title}</span>
          </button>
        ))}
      </div>
      <div className="pr-1 hidden sm:flex gap-x-1">
        {menuOpen && selectedOptionData && (
          <div
            className={`bg-white w-52 lg:w-80 h-[95vh] p-4 relative rounded-2xl ${
              selectedOption === "tarefas" ? "overflow-hidden" : "overflow-y-auto"
            }`}
          >
            <button
              className="absolute top-5 right-4 z-30 flex h-8 w-8 items-center justify-center text-gray-500 transition-all hover:text-gray-700"
              onClick={() => {
                setMenuOpen(false);
                setSelectedOption(null);
              }}
              title={t("common:close")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.89062 3.89062L11.6688 11.6688"
                  stroke="#797979"
                  stroke-linecap="round"
                />
                <path
                  d="M11.6719 3.89062L3.8937 11.6688"
                  stroke="#797979"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <div className="h-full">{selectedOptionData?.component}</div>
          </div>
        )}

        <div className="bg-white border-cinza-300 text-cinza-200 flex gap-1 flex-col h-fit items-center my-10 py-4 w-10 rounded-full">
          {filteredMenus?.map((option: any) =>
            ["cronograma-social"].includes(option.id) ? (
              <NavLink
                to={option.route}
                key={option.id}
                title={option.title}
                className={`p-1 rounded-md transition-all ${
                  selectedOption === option.id
                    ? "bg-azul-400 text-white"
                    : "hover:bg-azul-400 hover:text-white"
                } ${option.id === "home" && "sm:hidden"}`}
              >
                {option.icon}
              </NavLink>
            ) : (
              <button
                key={option.id}
                title={option.title}
                className={`p-1 rounded-md transition-all ${
                  selectedOption === option.id
                    ? "bg-azul-400 text-white"
                    : "hover:bg-azul-400 hover:text-white"
                } ${option.id === "home" && "sm:hidden"}`}
                onClick={() => {
                  setSelectedOption(option?.id);
                  setMenuOpen(true);
                }}
              >
                {option.icon}
              </button>
            ),
          )}
        </div>

        <div
          className={`fixed bottom-0 ${
            menuOpen ? "right-[380px]" : "right-10"
          } flex flex-wrap gap-2 p-4`}
        >
          <AnimatePresence mode="sync">
            {context.openChats.map((chat: any, index: number) => (
              <motion.div
                key={chat?.room?.id}
                initial={{ opacity: 0, scale: 1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{
                  duration: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                  delay: index * 0.05,
                }}
                style={{
                  position: "absolute",
                  bottom: "30px",
                  right: `${index * 450}px`,
                }}
                className={`transition-all ${
                  minimizedChats.includes(chat.room.id)
                    ? "h-12 w-80"
                    : "h-[34rem] w-[27.5rem]"
                } bg-white shadow-lg rounded-[27px] overflow-hidden`}
              >
                <ChatWindowImproved
                  user={chat?.guest}
                  room={chat.room}
                  onClose={(options) => handleCloseChat(chat.room.id, options)}
                  setMenuOpen={setMenuOpen}
                  setSelectedOption={setSelectedOption}
                  onMinimize={() =>
                    minimizedChats.includes(chat.room.id)
                      ? handleMaximizeChat(chat.room.id)
                      : handleMinimizeChat(chat.room.id)
                  }
                  isMinimized={minimizedChats.includes(chat.room.id)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
